import type { WashAppointment, WashUnitView } from "@station/contracts";
import { type LocalDate, toIstanbulDate } from "@station/core";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  DateInput,
  EmptyState,
  FormField,
  Select,
  SkeletonRows,
  useToast,
} from "@station/ui";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { CalendarClock, CalendarPlus, CalendarX2, UserCheck, UserX } from "lucide-react";
import { type ReactElement, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { compactSearch } from "../../../features/shifts/shift-values.js";
import {
  BookAppointmentDialog,
  CancelAppointmentDialog,
  CheckInDialog,
} from "../../../features/wash/appointment-dialogs.js";
import {
  useRefreshWash,
  useWashAppointments,
  useWashAvailability,
  useWashUnits,
} from "../../../features/wash/queries.js";
import {
  AppointmentStatusBadge,
  washSearchShape,
  WashStationPage,
  type WashStationContext,
} from "../../../features/wash/shared.js";
import {
  canMarkNoShow,
  displayPlate,
  formatClock,
  handWashDurationMinutes,
} from "../../../features/wash/wash-values.js";
import { api } from "../../../lib/api.js";
import { errorMessage } from "../../../lib/errors.js";
import { formatDate } from "../../../lib/format.js";

const searchSchema = z.object({
  ...washSearchShape,
  unitId: z.string().uuid().optional().catch(undefined),
  date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional()
    .catch(undefined),
});

export const Route = createFileRoute("/_app/yikama/randevular")({
  validateSearch: (search) => searchSchema.parse(search),
  component: AppointmentsRoute,
});

/**
 * El yıkama randevuları (Prompt 18 web, ADR 0018 §12): el yıkama ünitesi başına GÜN görünümü — seçilen hizmetlere göre
 * uygun slotlar (boş kapasiteyle; `GET /wash/units/:id/availability`), randevu alma, günün randevuları ve işlemler (geldi
 * → iş emri açılır; gelmedi — randevu saati geldikten sonra; iptal — gerekçeyle). Randevusuz iş emirleri takvime girmez.
 */
function AppointmentsRoute(): ReactElement {
  const { t } = useTranslation();
  return (
    <WashStationPage title={t("menu.washAppointments")} description={t("wash.appointments.description")}>
      {(context) => <AppointmentsBody context={context} />}
    </WashStationPage>
  );
}

function AppointmentsBody({ context }: { context: WashStationContext }): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const units = useWashUnits(context.station.id);
  const handUnits = (units.data ?? []).filter(
    (unit) => unit.unitType === "HAND_WASH" && unit.status === "ACTIVE",
  );
  const unit = handUnits.find((candidate) => candidate.id === search.unitId) ?? handUnits[0] ?? null;
  const date = search.date ?? toIstanbulDate(new Date());
  const setSearch = (patch: Record<string, string | undefined>) =>
    void navigate({
      to: ".",
      search: ((previous: Record<string, unknown>) => compactSearch({ ...previous, ...patch })) as never,
      replace: true,
    });

  if (units.error) return <Alert tone="danger">{errorMessage(units.error)}</Alert>;
  if (!units.data) return <SkeletonRows rows={6} />;
  if (unit === null)
    return (
      <EmptyState
        headingLevel={2}
        icon={<CalendarClock />}
        title={t("wash.appointments.noHandUnitTitle")}
        description={t("wash.appointments.noHandUnitDescription")}
        action={
          <Link
            to="/ayarlar/istasyonlar/$stationId"
            params={{ stationId: context.station.id }}
            className="text-sm underline underline-offset-4"
          >
            {t("wash.appointments.openStation")}
          </Link>
        }
      />
    );

  return (
    <>
      <Card className="flex flex-wrap items-end gap-3 p-4">
        {handUnits.length > 1 ? (
          <FormField label={t("wash.fields.unit")} className="w-56">
            {(control) => (
              <Select
                {...control}
                value={unit.id}
                onChange={(event) => setSearch({ unitId: event.target.value })}
              >
                {handUnits.map((candidate) => (
                  <option key={candidate.id} value={candidate.id}>
                    {candidate.name}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
        ) : (
          <p className="text-sm">
            <span className="text-muted-foreground">{t("wash.fields.unit")}: </span>
            <span className="font-medium">{unit.name}</span>
          </p>
        )}
        <FormField label={t("wash.appointments.day")} className="w-44">
          {(control) => (
            <DateInput
              {...control}
              value={date}
              onValueChange={(next) => setSearch({ date: next ?? undefined })}
            />
          )}
        </FormField>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setSearch({ date: toIstanbulDate(new Date()) })}
            disabled={date === toIstanbulDate(new Date())}
          >
            {t("wash.appointments.today")}
          </Button>
        </div>
      </Card>
      <DayView key={`${unit.id}-${date}`} context={context} unit={unit} date={date} />
    </>
  );
}

function DayView({
  context,
  unit,
  date,
}: {
  context: WashStationContext;
  unit: WashUnitView;
  date: string;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshWash();
  const services = unit.programs.filter((program) => program.isActive && program.kind === "HAND_WASH");
  const [programIds, setProgramIds] = useState<string[]>(() => services.slice(0, 1).map((p) => p.programId));
  const availability = useWashAvailability(unit.id, date, programIds);
  const appointments = useWashAppointments({
    stationId: context.station.id,
    from: date,
    to: date,
    unitId: unit.id,
  });
  const [booking, setBooking] = useState<{ startsAt: string | null } | null>(null);
  const [cancelling, setCancelling] = useState<WashAppointment | null>(null);
  const [checkingIn, setCheckingIn] = useState<WashAppointment | null>(null);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const handle = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(handle);
  }, []);
  const duration = handWashDurationMinutes(
    services.filter((program) => programIds.includes(program.programId)),
  );
  const operate = context.permissions.canOperate;

  const noShow = useMutation({
    mutationFn: async (appointment: WashAppointment) =>
      (
        await api.POST("/wash/appointments/{id}/no-show", {
          params: { path: { id: appointment.id } },
          body: { version: appointment.version },
        })
      ).data!,
    onSuccess: async () => {
      await refresh();
      toast({ tone: "success", title: t("wash.appointments.noShowDone") });
    },
    onError: async (caught) => {
      await refresh();
      toast({ tone: "danger", title: errorMessage(caught) });
    },
  });

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] [&>*]:min-w-0">
      <Card className="grid content-start gap-3 p-4" aria-labelledby="availability-heading">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 id="availability-heading" className="text-base font-semibold">
            {t("wash.appointments.availability")}
          </h2>
          {operate ? (
            <Button size="sm" onClick={() => setBooking({ startsAt: null })} disabled={services.length === 0}>
              <CalendarPlus aria-hidden="true" />
              {t("wash.appointments.book")}
            </Button>
          ) : null}
        </div>
        {services.length === 0 ? (
          <Alert tone="info">{t("wash.appointments.noServices")}</Alert>
        ) : (
          <fieldset className="grid gap-2">
            <legend className="mb-1 text-sm font-medium">{t("wash.appointments.services")}</legend>
            <div className="flex flex-wrap gap-2">
              {services.map((program) => (
                <label
                  key={program.programId}
                  className="flex min-h-11 items-center gap-2 rounded-md border px-3 text-sm"
                >
                  <Checkbox
                    checked={programIds.includes(program.programId)}
                    onChange={(event) =>
                      setProgramIds((current) =>
                        event.target.checked
                          ? [...current, program.programId]
                          : current.filter((id) => id !== program.programId),
                      )
                    }
                  />
                  {program.name}
                </label>
              ))}
            </div>
            {duration !== null ? (
              <p className="text-xs text-muted-foreground">
                {t("wash.appointments.duration", { minutes: duration })}
              </p>
            ) : null}
          </fieldset>
        )}
        {programIds.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("wash.appointments.pickService")}</p>
        ) : availability.error ? (
          <Alert tone="danger">{errorMessage(availability.error)}</Alert>
        ) : !availability.data ? (
          <SkeletonRows rows={3} />
        ) : availability.data.slots.length === 0 ? (
          <Alert tone="info">
            {availability.data.openingHours.length === 0
              ? t("wash.appointments.noOpeningHours")
              : t("wash.appointments.closedDay", { date: formatDate(date) })}
          </Alert>
        ) : (
          <>
            <p className="text-xs text-muted-foreground">
              {t("wash.appointments.capacity", {
                capacity: availability.data.capacity,
                slot: availability.data.slotMinutes,
              })}
            </p>
            <ul className="grid grid-cols-3 gap-2 sm:grid-cols-4" aria-label={t("wash.appointments.slots")}>
              {availability.data.slots.map((slot) => {
                const past = new Date(slot.startsAt).getTime() < now - 60_000;
                const disabled = !slot.bookable || past || !operate;
                return (
                  <li key={slot.startsAt}>
                    <button
                      type="button"
                      disabled={disabled}
                      onClick={() => setBooking({ startsAt: slot.startsAt })}
                      className={`grid min-h-14 w-full place-items-center rounded-md border px-2 py-1.5 text-center text-sm ${
                        slot.bookable && !past
                          ? "border-success/50 bg-success-subtle hover:border-success"
                          : "bg-muted text-muted-foreground"
                      } disabled:cursor-not-allowed`}
                    >
                      <span className="font-semibold tabular">{formatClock(slot.startsAt)}</span>{" "}
                      <span className="text-xs">
                        {slot.bookable
                          ? t("wash.appointments.free", { count: slot.free })
                          : t("wash.appointments.full")}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </Card>

      <Card className="grid content-start gap-3 p-4" aria-labelledby="day-heading">
        <h2 id="day-heading" className="text-base font-semibold">
          {t("wash.appointments.dayList", { date: formatDate(date) })}
        </h2>
        {appointments.error ? (
          <Alert tone="danger">{errorMessage(appointments.error)}</Alert>
        ) : !appointments.data ? (
          <SkeletonRows rows={4} />
        ) : appointments.data.length === 0 ? (
          <EmptyState
            headingLevel={3}
            icon={<CalendarX2 />}
            title={t("wash.appointments.emptyTitle")}
            description={t("wash.appointments.emptyDescription")}
          />
        ) : (
          <ul className="grid gap-2" aria-label={t("wash.appointments.dayList", { date: formatDate(date) })}>
            {appointments.data.map((appointment) => (
              <li
                key={appointment.id}
                className="grid gap-2 rounded-md border p-3"
                data-testid="appointment-item"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-lg font-semibold tabular">
                    {formatClock(appointment.startsAt)}–{formatClock(appointment.endsAt)}
                  </span>
                  <AppointmentStatusBadge status={appointment.status} />
                  <span className="font-mono tracking-wide">
                    {displayPlate(appointment.plate, appointment.personalDataMasked)}
                  </span>
                </div>
                <p className="text-sm">
                  {appointment.programs.map((program) => program.name).join(", ")}
                  {appointment.contactName ? ` · ${appointment.contactName}` : ""}
                  {appointment.contactPhone ? ` · ${appointment.contactPhone}` : ""}
                </p>
                {appointment.note ? (
                  <p className="text-xs text-muted-foreground">{appointment.note}</p>
                ) : null}
                {appointment.cancelReason ? (
                  <p className="text-xs text-muted-foreground">
                    {t("wash.appointments.cancelReason", { reason: appointment.cancelReason })}
                  </p>
                ) : null}
                <div className="flex flex-wrap gap-2">
                  {appointment.workOrderId ? (
                    <Button variant="outline" size="sm" asChild>
                      <Link
                        to="/yikama/is-emirleri/$id"
                        params={{ id: appointment.workOrderId }}
                        search={{ station: context.station.id } as never}
                      >
                        {t("wash.appointments.openWorkOrder", { no: appointment.workOrderNo ?? "" })}
                      </Link>
                    </Button>
                  ) : null}
                  {operate && appointment.allowedTransitions.includes("CHECKED_IN") ? (
                    <Button
                      size="sm"
                      onClick={() => setCheckingIn(appointment)}
                      aria-label={t("wash.appointments.checkInFor", {
                        at: formatClock(appointment.startsAt),
                      })}
                    >
                      <UserCheck aria-hidden="true" />
                      {t("wash.appointments.checkIn")}
                    </Button>
                  ) : null}
                  {operate && canMarkNoShow(appointment, now) ? (
                    <Button
                      size="sm"
                      variant="outline"
                      loading={noShow.isPending && noShow.variables?.id === appointment.id}
                      onClick={() => noShow.mutate(appointment)}
                      aria-label={t("wash.appointments.noShowFor", { at: formatClock(appointment.startsAt) })}
                    >
                      <UserX aria-hidden="true" />
                      {t("wash.appointments.noShow")}
                    </Button>
                  ) : null}
                  {operate && appointment.allowedTransitions.includes("CANCELLED") ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setCancelling(appointment)}
                      aria-label={t("wash.appointments.cancelFor", { at: formatClock(appointment.startsAt) })}
                    >
                      {t("wash.appointments.cancel")}
                    </Button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {booking ? (
        <BookAppointmentDialog
          open
          onOpenChange={(open) => !open && setBooking(null)}
          unit={unit}
          date={date as LocalDate}
          initialStartsAt={booking.startsAt}
          initialProgramIds={programIds}
        />
      ) : null}
      {cancelling ? (
        <CancelAppointmentDialog
          open
          onOpenChange={(open) => !open && setCancelling(null)}
          appointment={cancelling}
        />
      ) : null}
      {checkingIn ? (
        <CheckInDialog
          open
          onOpenChange={(open) => !open && setCheckingIn(null)}
          appointment={checkingIn}
          station={context.station}
        />
      ) : null}
    </div>
  );
}
